import { FC, useState, useEffect } from 'react';
import {
  Alert,
  Button as DesignSystemButton,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Text,
  TextInput,
} from 'design-system';
import { useStellarTask } from '../../../../hooks/stellarGenAITask/useStellarGenAITask';
import API from '@scale/frontend-lib/dashboard-api';
import {
  GetStellarGenAITaskByAttemptParams,
  GetStellarGenAITaskEligibilityParams,
  GetStellarGenAITaskEligibilityRespBody,
  StellarGenAITaskEligibilityMetadata,
  CreateStellarGenAITaskReqBody,
  UpdateStellarGenAITaskParams,
  UpdateStellarGenAITaskReqBody,
  StellarGenAITaskStatus,
} from '@scale/shared/types/stellarGenAITask';
import { Button, Icon } from '@scale/scaleui-radix';
import { faStar } from '@fortawesome/pro-solid-svg-icons';

interface StellarTaskDialogProps {
  feedback: string;
  setFeedback: (feedback: string) => void;
  taskAttemptId: string;
  reviewedByUserId: string;
  dontDisableOnEmptyFeedback?: boolean;
}

export const StellarTaskDialog: FC<StellarTaskDialogProps> = ({
  feedback,
  setFeedback,
  taskAttemptId,
  reviewedByUserId,
  dontDisableOnEmptyFeedback,
}) => {
  const [eligible, setEligible] = useState<boolean>(false);
  const [eligibilityMetadata, setEligibilityMetadata] =
    useState<StellarGenAITaskEligibilityMetadata | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [mutationError, setMutationError] = useState<string>('');
  const {
    data: stellarTask,
    getState: getStellarTaskState,
    executeGet: getStellarTask,
    createState: createStellarTaskState,
    executeCreate: createStellarTask,
    updateState: updateStellarTaskState,
    executeUpdate: updateStellarTask,
  } = useStellarTask();

  // Check if taskAttempt is eligible to be considered as stellar.
  async function getStellarTaskEligibility(taskAttemptId: string) {
    try {
      const params: GetStellarGenAITaskEligibilityParams = { taskAttemptId };
      const resp: GetStellarGenAITaskEligibilityRespBody =
        await API.genai.getStellarGenAITaskEligibility(params);
      setEligibilityMetadata(resp.metadata);
      setEligible(resp.eligible);
    } catch (e) {
      setEligibilityMetadata(null);
      setEligible(false);
    }
  }

  // Submit form.
  // Either create a new stellar task or update an existing one.
  const handleSubmit = async (status: StellarGenAITaskStatus) => {
    if (!stellarTask) {
      if (!eligibilityMetadata) {
        setMutationError('Error marking task as stellar: eligibilityMetadata is null.');
        return;
      }

      // No stellar task exists for this attempt, create one.
      const reqBody: CreateStellarGenAITaskReqBody = {
        taskId: eligibilityMetadata.taskId,
        taskAttemptId,
        workerId: eligibilityMetadata.attemptedBy,
        projectId: eligibilityMetadata.projectId,
        reviewedByUserId,
        status,
        feedbackString: feedback,
      };
      await createStellarTask(reqBody);

      if (createStellarTaskState.error) {
        setMutationError('Error marking task as stellar: ' + createStellarTaskState.error.message);
      } else {
        setIsOpen(false);
      }
    } else {
      // Stellar task exists, update it.
      const params: UpdateStellarGenAITaskParams = {
        stellarTaskId: stellarTask._id,
      };
      const reqBody: UpdateStellarGenAITaskReqBody = {
        status,
        feedbackString: feedback,
      };
      await updateStellarTask(params, reqBody);

      if (updateStellarTaskState.error) {
        setMutationError('Error updating stellar task: ' + updateStellarTaskState.error.message);
      } else {
        setIsOpen(false);
      }
    }
  };

  useEffect(() => {
    getStellarTaskEligibility(taskAttemptId);
    const params: GetStellarGenAITaskByAttemptParams = {
      taskAttemptId,
    };
    getStellarTask(params);
  }, [getStellarTask, taskAttemptId]);

  if (!eligible) {
    return null;
  }

  const isMarkedAsStellar = stellarTask && stellarTask.status === StellarGenAITaskStatus.Confirmed;

  return (
    <div>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
        <DialogTitle>Stellar task: attemptId {taskAttemptId}</DialogTitle>
        <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {mutationError && (
            <Alert severity={'error'} onClose={() => setMutationError('')}>
              <Text>{mutationError}</Text>
            </Alert>
          )}
          <Alert severity={'info'}>
            Marking an attempt as stellar will display it to Contributors.
          </Alert>
          <TextInput
            placeholder="Attempt Feedback"
            multiline
            value={feedback}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFeedback(event.target.value);
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'end' }}>
            {createStellarTaskState.loading || updateStellarTaskState.loading ? (
              <CircularProgress />
            ) : (
              <div style={{ display: 'flex', gap: '10px' }}>
                {isMarkedAsStellar && (
                  <DesignSystemButton
                    type="error"
                    variant="outlined"
                    onClick={() => handleSubmit(StellarGenAITaskStatus.Rejected)}
                  >
                    Unmark
                  </DesignSystemButton>
                )}
                <DesignSystemButton
                  type="primary"
                  variant="contained"
                  onClick={() => handleSubmit(StellarGenAITaskStatus.Confirmed)}
                  disabled={feedback.length === 0}
                >
                  {isMarkedAsStellar ? 'Update' : 'Mark as stellar'}
                </DesignSystemButton>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {getStellarTaskState.loading ? (
        <CircularProgress />
      ) : (
        <Button
          variant={isMarkedAsStellar ? 'solid' : 'outline'}
          onClick={() => setIsOpen(true)}
          disabled={!isMarkedAsStellar && !dontDisableOnEmptyFeedback && feedback.length === 0}
        >
          <Icon label="mark-as-stellar" icon={faStar} />
          {isMarkedAsStellar ? 'Marked as stellar' : 'Mark as stellar'}
        </Button>
      )}
    </div>
  );
};
